<template>
  <div id="app">
    <button
      class="theme-toggle"
      type="button"
      :aria-pressed="theme === 'dark'"
      :title="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <span class="theme-toggle__dot" aria-hidden="true"></span>
      <span class="theme-toggle__label">{{ theme === "dark" ? "Dark" : "Light" }} mode</span>
    </button>
    <router-view></router-view>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useStoreModule } from "@/composables/useStoreModule.js";

export default {
  name: "App",
  setup() {
    const theme = ref("light");
    const mapStore = useStoreModule("map");
    const activeMapStyleId = computed(
      () => mapStore.getters.getActiveMapStyleId
    );
    const setActiveMapStyleId = mapStore.actions.setActiveMapStyleId;

    const applyTheme = (value) => {
      document.documentElement.setAttribute("data-theme", value);
    };

    const resolveInitialTheme = () => {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") {
        return stored;
      }

      return "dark";
    };

    const syncMapStyleForTheme = (value) => {
      const currentStyle = activeMapStyleId.value;
      const isDefaultStyle =
        currentStyle === "mapbox-dark" || currentStyle === "mapbox-lights";
      if (!isDefaultStyle) {
        return;
      }

      const nextStyle = value === "dark" ? "mapbox-dark" : "mapbox-lights";
      if (currentStyle !== nextStyle) {
        setActiveMapStyleId(nextStyle);
      }
    };

    const setTheme = (value) => {
      theme.value = value;
      applyTheme(value);
      localStorage.setItem("theme", value);
      syncMapStyleForTheme(value);
    };

    const toggleTheme = () => {
      setTheme(theme.value === "dark" ? "light" : "dark");
    };

    onMounted(() => {
      setTheme(resolveInitialTheme());
    });

    return {
      theme,
      toggleTheme,
    };
  },
};
</script>

<style lang="scss">
#app {
  min-height: 100%;
  position: relative;
}

.theme-toggle {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 10001;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--color-secondary);
  color: var(--font-color);
  font-size: 12px;
  letter-spacing: 0.02em;
  box-shadow: var(--smooth-shadow);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.theme-toggle:hover {
  transform: translateY(-1px);
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.18);
}

.theme-toggle__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-primary);
}

.theme-toggle__label {
  text-transform: uppercase;
}
</style>
